/**
 * Collection Filters (E20-1050)
 *
 * Progressive enhancement for the BFX filter scaffold (snippets/bfx-collection-filters.liquid).
 * The filtering itself is Impulse's — the .filter-form AJAX-submits on `input` and swaps
 * #CollectionAjaxContent. This element adds two behaviors Shopify's native filters can't express:
 *
 *   1. Single-select deselect — radio groups (data-single-select) give single-select for free
 *      (shared name), but a native radio can't be un-checked by clicking it again. Re-clicking the
 *      active option here clears it and re-submits, returning the grid to unfiltered.
 *
 *   2. Open-state ownership — groups always render collapsed (design decision). Open/closed is
 *      entirely user-driven: once a shopper opens/closes a group we remember it per collection
 *      (sessionStorage) and restore it after each AJAX re-render, so their choice sticks while
 *      they filter. No auto-open on active filters.
 *
 * Listeners are scoped with AbortController and torn down in disconnectedCallback so nothing leaks
 * across AJAX swaps (each swap disconnects the old element and connects a fresh one).
 */
const STORE_KEY = 'bfx-filters-open';

// The keys noUiSlider moves a price handle with. Anything else reaching the keyup listener —
// Tab most of all — changed no value and must not dim the panel.
const PRICE_ADJUST_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
]);

const readOpenState = (scope) => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}')[scope] || {};
  } catch {
    return {};
  }
};

const writeOpenState = (scope, state) => {
  try {
    const all = JSON.parse(sessionStorage.getItem(STORE_KEY) || '{}');
    all[scope] = state;
    sessionStorage.setItem(STORE_KEY, JSON.stringify(all));
  } catch {
    // sessionStorage unavailable (private mode / quota) — persistence is a nicety, so ignore.
  }
};

// Whether the first server-rendered pass has been painted. Every later connect is an AJAX
// re-render, which is the only time the enter animation should run — otherwise the sidebar would
// fade in on every ordinary page load.
let initialRenderPainted = false;

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.scope = this.dataset.scope || 'default';
    this.abortController = new AbortController();

    this.syncSelected();
    this.restoreOpenState();
    this.playEnterTransition();
    this.namePriceHandles();

    this.addEventListener(
      'click',
      (event) => {
        const trigger = event.target.closest('.collapsible-trigger');
        if (trigger) {
          // Record the new open/closed state after Impulse's collapsible handler toggles it.
          requestAnimationFrame(() => this.persistOpenState());
          return;
        }
        this.handleSingleSelect(event);
      },
      { signal: this.abortController.signal }
    );

    // Dim as soon as a filter changes, while this element is still on the page. Groups that no
    // longer apply are dropped server-side, so by the time the replacement arrives they simply
    // aren't in it — there is no outgoing DOM left to animate. Dimming here and fading the
    // replacement in makes that read as "the filters are updating" rather than content vanishing.
    this.addEventListener('input', () => this.markUpdating(), {
      signal: this.abortController.signal,
    });

    // The price slider never reaches that listener. Impulse's noUiSlider `change` handler assigns
    // the min/max inputs' `.value` directly — a property write fires no event — and then calls its
    // own onChange, so nothing bubbles. Dim on the interaction that precedes it instead: releasing
    // a drag, or arrowing a handle. Matters most on price-only collections, where it's the only
    // filter there is.
    this.addEventListener(
      'pointerup',
      (event) => {
        if (event.target.closest('.price-range')) this.markUpdating();
      },
      { signal: this.abortController.signal }
    );

    // Keyboard path is limited to the keys noUiSlider actually moves a handle with — Tab reaches
    // this listener too, and merely focusing a handle shouldn't dim a panel nothing has changed.
    this.addEventListener(
      'keyup',
      (event) => {
        if (!PRICE_ADJUST_KEYS.has(event.key)) return;
        if (event.target.closest('.price-range')) this.markUpdating();
      },
      { signal: this.abortController.signal }
    );
  }

  // Dim until the replacement arrives. The failsafe matters for the price slider: releasing a drag
  // back on its original value submits nothing, so no re-render follows and without it the sidebar
  // would sit dimmed for good. Harmless for the other controls, whose swap lands well inside it.
  markUpdating() {
    this.classList.add('is-updating');
    clearTimeout(this.updatingFailsafe);
    this.updatingFailsafe = setTimeout(() => this.classList.remove('is-updating'), 1200);
  }

  // Fade in only on an AJAX re-render. Two frames: the first paints the transparent start state,
  // the second removes it so the transition actually runs (a single frame would coalesce both).
  playEnterTransition() {
    if (!initialRenderPainted) {
      requestAnimationFrame(() => {
        initialRenderPainted = true;
      });
      return;
    }

    this.classList.add('is-entering');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.remove('is-entering'));
    });
  }

  // Re-clicking the already-selected radio clears the group and re-submits (Impulse listens on input).
  handleSingleSelect(event) {
    const input = event.target.closest('input[data-single-select]');
    if (!input) return;

    const group = input.closest('.bfx-collection-filters__group');
    if (!group) return;

    if (group.dataset.selected === input.value) {
      input.checked = false;
      group.dataset.selected = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      group.dataset.selected = input.value;
    }
  }

  // Seed each radio group's active value from the server-rendered checked state.
  syncSelected() {
    this.querySelectorAll('.bfx-collection-filters__group').forEach((group) => {
      const checked = group.querySelector('input[data-single-select]:checked');
      group.dataset.selected = checked ? checked.value : '';
    });
  }

  restoreOpenState() {
    const state = readOpenState(this.scope);
    this.querySelectorAll('.bfx-collection-filters__group').forEach((group) => {
      const id = group.dataset.filterId;
      if (state[id]) this.openGroup(group);
    });
  }

  persistOpenState() {
    const state = readOpenState(this.scope);
    this.querySelectorAll('.bfx-collection-filters__group').forEach((group) => {
      const trigger = group.querySelector('.collapsible-trigger');
      if (group.dataset.filterId && trigger) {
        state[group.dataset.filterId] = trigger.classList.contains('is-open');
      }
    });
    writeOpenState(this.scope, state);
  }

  openGroup(group) {
    const trigger = group.querySelector('.collapsible-trigger');
    const content = group.querySelector('.collapsible-content');
    if (!trigger || !content) return;

    trigger.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
    content.classList.add('is-open');
    content.style.height = 'auto';
  }

  // noUiSlider gives each price handle role="slider" plus aria-valuemin/max/now and arrow-key
  // support, but no accessible name — so both handles announce identically and a screen-reader
  // user can't tell the lower bound from the upper. The hidden min/max inputs can't carry the
  // name either: theme.css holds them at display:none, so they aren't in the accessibility tree.
  //
  // The handles are built by Impulse's PriceRange in theme.js, which may not have run yet when
  // this connects, so watch for them rather than assuming. Runs on every connect, which covers
  // the AJAX re-renders too — each one destroys and rebuilds the slider.
  namePriceHandles() {
    const range = this.querySelector('.price-range');
    if (!range) return;

    const labels = [range.dataset.minLabel, range.dataset.maxLabel];
    if (!labels[0] && !labels[1]) return;

    const apply = () => {
      const handles = range.querySelectorAll('[role="slider"]');
      if (!handles.length) return false;
      handles.forEach((handle, i) => {
        if (labels[i]) handle.setAttribute('aria-label', labels[i]);
      });
      return true;
    };

    if (apply()) return;

    // Not built yet — stop watching as soon as it is, and on teardown either way.
    const observer = new MutationObserver(() => {
      if (apply()) observer.disconnect();
    });
    observer.observe(range, { childList: true, subtree: true });
    this.abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    clearTimeout(this.updatingFailsafe);
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
